import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  DashboardRole,
  getDashboardProfitAnalysis,
  getDashboardProfitAnalysisDetail,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  ChoiceUnitConvertedRow,
  GiftItemProfitRow,
  ProfitAnalysisDetailResponse,
  ProfitAnalysisResponse,
  ProfitTypeSlice,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';
import { LowerRole } from '@shared/types';
import { formatPrice } from '@shared/utils/formatPrice';

import { fmtPeriod, fmtRate } from '../utils/dashboard.utils';
import {
  CenterLoading,
  EmptyBox,
  ErrorBox,
  StatCard,
  TD_CLASS,
  TH_CLASS,
} from './DashboardShared';

function convertedRevenueShare(g: GiftItemProfitRow): number {
  if (g.totalCount <= 0) return 0;
  return g.totalRevenue * (g.convertedCount / g.totalCount);
}

function windfallRevenueShare(g: GiftItemProfitRow): number {
  if (g.totalCount <= 0) return 0;
  return g.totalRevenue * (g.windfallCount / g.totalCount);
}

function effectiveConvertedProfitRate(g: GiftItemProfitRow): number {
  if (g.convertedProfitRate != null) return g.convertedProfitRate;
  const rev = convertedRevenueShare(g);
  return rev > 0 ? g.convertedProfit / rev : 0;
}

function effectiveWindfallProfitRate(g: GiftItemProfitRow): number {
  if (g.windfallProfitRate != null) return g.windfallProfitRate;
  const rev = windfallRevenueShare(g);
  return rev > 0 ? g.windfallProfit / rev : 0;
}

type SliceTab = 'all' | 'coupon' | 'delivery';
type SortKey = 'totalCount' | 'totalProfitRate' | 'totalProfit' | 'windfallRate';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'totalCount', label: '건수' },
  { key: 'totalProfit', label: '수익' },
  { key: 'totalProfitRate', label: '수익률' },
  { key: 'windfallRate', label: '낙전율' },
];

const EMPTY_SLICE: ProfitTypeSlice = {
  totalCount: 0,
  convertedCount: 0,
  windfallCount: 0,
  conversionRate: 0,
  windfallRate: 0,
  revenue: 0,
  convertedRevenue: 0,
  windfallRevenue: 0,
  serviceFee: 0,
  cogs: 0,
  convertedProfit: 0,
  convertedProfitRate: 0,
  windfallProfit: 0,
  windfallProfitRate: 0,
  totalProfit: 0,
  totalProfitRate: 0,
};

function safeSlice(src?: ProfitTypeSlice): ProfitTypeSlice {
  if (!src) return EMPTY_SLICE;
  const raw = src as unknown as Record<string, number>;
  return Object.fromEntries(
    Object.entries(EMPTY_SLICE).map(([k, def]) => [k, raw[k] ?? def]),
  ) as unknown as ProfitTypeSlice;
}

function summaryToSlice(s: ProfitAnalysisResponse['summary']): ProfitTypeSlice {
  return {
    totalCount: s.totalItemCount ?? 0,
    convertedCount: s.convertedCount ?? 0,
    windfallCount: s.windfallCount ?? 0,
    conversionRate: s.conversionRate ?? 0,
    windfallRate: s.windfallRate ?? 0,
    revenue: s.totalRevenue ?? 0,
    convertedRevenue: s.totalConvertedRevenue ?? 0,
    windfallRevenue: s.totalWindfallRevenue ?? 0,
    serviceFee: s.totalServiceFee ?? 0,
    cogs: s.totalCogs ?? 0,
    convertedProfit: s.totalConvertedProfit ?? 0,
    convertedProfitRate: s.convertedProfitRate ?? 0,
    windfallProfit: s.totalWindfallProfit ?? 0,
    windfallProfitRate: s.windfallProfitRate ?? 0,
    totalProfit: s.totalProfit ?? 0,
    totalProfitRate: s.totalProfitRate ?? 0,
  };
}

export const DashboardProfitTab: FC<{
  from: string;
  to: string;
  role?: DashboardRole;
}> = ({ from, to, role }) => {
  const isAgencyAdmin = role === LowerRole.BugoAgencyAdmin;

  const [data, setData] = useState<ProfitAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sliceTab, setSliceTab] = useState<SliceTab>('all');

  const [detailData, setDetailData] = useState<ProfitAnalysisDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('totalCount');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getDashboardProfitAnalysis(from, to, role));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '데이터를 불러올 수 없습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, role]);

  const fetchDetail = useCallback(async () => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      setDetailData(await getDashboardProfitAnalysisDetail(from, to, role));
    } catch (e: unknown) {
      setDetailError(
        e instanceof Error ? e.message : '상세 데이터를 불러올 수 없습니다.',
      );
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  }, [from, to, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const sortedItems: GiftItemProfitRow[] = useMemo(() => {
    if (!detailData) return [];
    const filtered =
      sliceTab === 'all'
        ? detailData.giftItems
        : detailData.giftItems.filter(
            (g) => g.type === (sliceTab === 'coupon' ? 'Coupon' : 'Delivery'),
          );
    return filtered.slice().sort((a, b) => b[sortKey] - a[sortKey]);
  }, [detailData, sliceTab, sortKey]);

  const detailTotals = useMemo(() => {
    if (!sortedItems.length) return null;
    return sortedItems.reduce(
      (acc, g) => ({
        totalCount: acc.totalCount + g.totalCount,
        convertedCount: acc.convertedCount + g.convertedCount,
        windfallCount: acc.windfallCount + g.windfallCount,
        totalRevenue: acc.totalRevenue + g.totalRevenue,
        totalServiceFee: acc.totalServiceFee + g.totalServiceFee,
        totalCogs: acc.totalCogs + g.totalCogs,
        convertedProfit: acc.convertedProfit + g.convertedProfit,
        windfallProfit: acc.windfallProfit + g.windfallProfit,
        totalProfit: acc.totalProfit + g.totalProfit,
        convertedRevenueShare: acc.convertedRevenueShare + convertedRevenueShare(g),
        windfallRevenueShare: acc.windfallRevenueShare + windfallRevenueShare(g),
      }),
      {
        totalCount: 0,
        convertedCount: 0,
        windfallCount: 0,
        totalRevenue: 0,
        totalServiceFee: 0,
        totalCogs: 0,
        convertedProfit: 0,
        windfallProfit: 0,
        totalProfit: 0,
        convertedRevenueShare: 0,
        windfallRevenueShare: 0,
      },
    );
  }, [sortedItems]);

  if (loading) return <CenterLoading />;
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data) return <EmptyBox />;

  const s = data.summary;
  const activeSlice: ProfitTypeSlice =
    sliceTab === 'coupon'
      ? safeSlice(s.coupon)
      : sliceTab === 'delivery'
      ? safeSlice(s.delivery)
      : summaryToSlice(s);

  return (
    <div className="space-y-6">
      {/* 헤더 + 유형 탭 */}
      <section className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              수익 분석
              <span className="ml-2 text-xs font-normal text-gray-400">
                결제일 기준 ({fmtPeriod(from, to)})
              </span>
            </h2>
            <p className="mt-1 text-xs text-gray-400">
              상품별로 받은 건(전환)과 안 받은 건(낙전)을 나눠서 수익을 분석합니다.
            </p>
          </div>
          <div className="flex rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
            {(['all', 'coupon', 'delivery'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setSliceTab(t)}
                className={`rounded px-3 py-1 transition-colors ${
                  sliceTab === t
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'all' ? '전체' : t === 'coupon' ? '쿠폰' : '배송'}
              </button>
            ))}
          </div>
        </div>

        {/* 개요 카드 */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label="총 매출"
            value={`${formatPrice(activeSlice.revenue)}원`}
            sub={
              sliceTab === 'all'
                ? `${s.orderCount}건 주문`
                : `${activeSlice.totalCount}건`
            }
            accent="indigo"
          />
          <StatCard
            label="수수료"
            value={`${formatPrice(activeSlice.serviceFee)}원`}
            accent="red"
          />
          <StatCard
            label="매입 원가"
            value={`${formatPrice(activeSlice.cogs)}원`}
            sub={`${activeSlice.convertedCount}건 전환`}
            accent="orange"
          />
          <StatCard
            label="총 수익"
            value={`${formatPrice(activeSlice.totalProfit)}원`}
            sub={`수익률 ${fmtRate(activeSlice.totalProfitRate)}`}
            accent="emerald"
          />
        </div>

        {/* 전환·낙전 분석 */}
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <HighlightCard
            label="전환 수익"
            value={activeSlice.convertedProfit}
            rate={activeSlice.convertedProfitRate}
            count={activeSlice.convertedCount}
            total={activeSlice.totalCount}
            revenue={activeSlice.convertedRevenue}
            revenueLabel="전환 매출"
            ratioLabel="전환율"
            ratio={activeSlice.conversionRate}
            color="emerald"
          />
          <HighlightCard
            label="낙전 수익"
            value={activeSlice.windfallProfit}
            rate={activeSlice.windfallProfitRate}
            count={activeSlice.windfallCount}
            total={activeSlice.totalCount}
            revenue={activeSlice.windfallRevenue}
            revenueLabel="낙전 매출"
            ratioLabel="낙전율"
            ratio={activeSlice.windfallRate}
            color="amber"
          />
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-medium text-gray-500">건수 분포</p>
            <div className="mt-2 space-y-1.5">
              <CountBar
                label="전환"
                count={activeSlice.convertedCount}
                total={activeSlice.totalCount}
                color="bg-emerald-500"
              />
              <CountBar
                label="낙전"
                count={activeSlice.windfallCount}
                total={activeSlice.totalCount}
                color="bg-amber-400"
              />
            </div>
            <p className="mt-2 text-right text-xs text-gray-400">
              전체 {activeSlice.totalCount}건
            </p>
          </div>
        </div>
      </section>

      {/* 상품별 수익 상세 (항상 표시) */}
      <section className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {isAgencyAdmin ? '판매상품 수익 상세' : '상품별 수익 상세'}
            </h2>
            <p className="mt-0.5 text-xs text-gray-400">
              {isAgencyAdmin
                ? '판매상품 단위로 전환·낙전 수익을 확인합니다. 행을 클릭하면 선택 옵션 상세를 펼칩니다.'
                : 'giftItem 단위로 전환·낙전 수익을 확인합니다. 행을 클릭하면 선택옵션(choiceUnit) 상세를 펼칩니다.'}
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-gray-400">정렬:</span>
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortKey(opt.key)}
                className={`rounded px-2 py-1 font-medium transition-colors ${
                  sortKey === opt.key
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {detailLoading && <CenterLoading />}
        {detailError && <ErrorBox message={detailError} onRetry={fetchDetail} />}
        {!detailLoading &&
          !detailError &&
          detailData &&
          (sortedItems.length === 0 ? (
            <EmptyBox />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className={TH_CLASS}>상품명</th>
                    <th className={TH_CLASS}>유형</th>
                    <th className={`${TH_CLASS} text-right`}>소매가</th>
                    <th className={`${TH_CLASS} text-right`}>도매가</th>
                    <th className={`${TH_CLASS} text-right`}>건수</th>
                    <th className={`${TH_CLASS} text-right`}>낙전율</th>
                    <th className={`${TH_CLASS} text-right`}>매출</th>
                    <th className={`${TH_CLASS} text-right`}>수수료</th>
                    <th className={`${TH_CLASS} text-right`}>매입</th>
                    <th className={`${TH_CLASS} text-right`}>
                      <div className="flex flex-col items-end gap-0.5 leading-tight">
                        <span>전환수익</span>
                        <span className="text-[10px] font-normal normal-case text-gray-400">
                          (전환수익률)
                        </span>
                      </div>
                    </th>
                    <th className={`${TH_CLASS} text-right`}>
                      <div className="flex flex-col items-end gap-0.5 leading-tight">
                        <span>낙전수익</span>
                        <span className="text-[10px] font-normal normal-case text-gray-400">
                          (낙전수익률)
                        </span>
                      </div>
                    </th>
                    <th className={`${TH_CLASS} text-right`}>총수익</th>
                    <th className={`${TH_CLASS} text-right`}>수익률</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => (
                    <GiftItemRow
                      key={item.giftItemId}
                      item={item}
                      isAgencyAdmin={isAgencyAdmin}
                    />
                  ))}
                </tbody>
                {detailTotals && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 bg-gray-100 font-semibold">
                      <td className={TD_CLASS} colSpan={2}>
                        합계 ({sortedItems.length}개 상품)
                      </td>
                      <td className={`${TD_CLASS} text-right text-gray-400`}>—</td>
                      <td className={`${TD_CLASS} text-right text-gray-400`}>—</td>
                      <td className={`${TD_CLASS} text-right`}>
                        <span className="text-emerald-700">
                          {detailTotals.convertedCount}
                        </span>
                        <span className="font-normal text-gray-500">/</span>
                        <span>{detailTotals.totalCount}</span>
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        {fmtRate(
                          detailTotals.totalCount > 0
                            ? detailTotals.windfallCount / detailTotals.totalCount
                            : 0,
                        )}
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        {formatPrice(detailTotals.totalRevenue)}
                      </td>
                      <td className={`${TD_CLASS} text-right text-red-600`}>
                        -{formatPrice(detailTotals.totalServiceFee)}
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        {detailTotals.totalCogs > 0
                          ? `-${formatPrice(detailTotals.totalCogs)}`
                          : '-'}
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        <div className="flex flex-col items-end gap-0 leading-tight">
                          <span className="font-medium text-emerald-700">
                            {formatPrice(detailTotals.convertedProfit)}
                          </span>
                          <span className="font-normal text-emerald-800/90">
                            (
                            {fmtRate(
                              detailTotals.convertedRevenueShare > 0
                                ? detailTotals.convertedProfit /
                                    detailTotals.convertedRevenueShare
                                : 0,
                            )}
                            )
                          </span>
                        </div>
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        <div className="flex flex-col items-end gap-0 leading-tight">
                          <span className="font-medium text-amber-700">
                            {formatPrice(detailTotals.windfallProfit)}
                          </span>
                          <span className="font-normal text-amber-900/90">
                            (
                            {fmtRate(
                              detailTotals.windfallRevenueShare > 0
                                ? detailTotals.windfallProfit /
                                    detailTotals.windfallRevenueShare
                                : 0,
                            )}
                            )
                          </span>
                        </div>
                      </td>
                      <td className={`${TD_CLASS} text-right text-indigo-700`}>
                        {formatPrice(detailTotals.totalProfit)}
                      </td>
                      <td className={`${TD_CLASS} text-right text-indigo-700`}>
                        {fmtRate(
                          detailTotals.totalRevenue > 0
                            ? detailTotals.totalProfit / detailTotals.totalRevenue
                            : 0,
                        )}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ))}
      </section>

      {/* 교차 검증 */}
      {data.crossCheck && (
        <section className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-3 text-lg font-bold text-gray-800">금액 검증</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              label="결제 금액 합계"
              value={`${formatPrice(data.crossCheck.byPayment)}원`}
            />
            <StatCard
              label="상품 판매가 합계"
              value={`${formatPrice(data.crossCheck.byItemRevenue)}원`}
            />
            <StatCard
              label="차이"
              value={`${formatPrice(data.crossCheck.diff)}원`}
              accent={data.crossCheck.matched ? 'emerald' : 'red'}
            />
            <div className="flex items-center justify-center rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
              <span
                className={`rounded-full px-3 py-1 text-sm font-bold ${
                  data.crossCheck.matched
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {data.crossCheck.matched ? '일치' : '불일치'}
              </span>
            </div>
          </div>
          {!data.crossCheck.matched && (
            <p className="mt-3 text-xs text-red-500">
              결제 금액과 상품 판매가 합계가 일치하지 않습니다. 부분 취소 등으로 차이가
              발생할 수 있습니다.
            </p>
          )}
        </section>
      )}
    </div>
  );
};

/* ── 하위 컴포넌트 ── */

const TD_R = `${TD_CLASS} text-right`;
const COL_SPAN = 13;

const GiftItemRow: FC<{ item: GiftItemProfitRow; isAgencyAdmin?: boolean }> = ({
  item: g,
  isAgencyAdmin,
}) => {
  const [open, setOpen] = useState(false);
  const hasChoiceUnits = g.choiceUnits.length > 0;
  return (
    <>
      {/* 판매상품(행) 메인 */}
      <tr
        onClick={hasChoiceUnits ? () => setOpen((v) => !v) : undefined}
        className={`border-b border-gray-100 transition-colors ${
          hasChoiceUnits ? 'cursor-pointer hover:bg-gray-50' : ''
        }`}
      >
        <td className={`${TD_CLASS} max-w-[min(42vw,15rem)] overflow-hidden`}>
          <div className="flex min-w-0 items-center gap-1.5">
            {hasChoiceUnits && (
              <svg
                className={`h-3 w-3 shrink-0 text-gray-400 transition-transform ${
                  open ? 'rotate-90' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            )}
            <span
              className="min-w-0 flex-1 truncate font-medium text-gray-800"
              title={g.giftItemName}
            >
              {g.giftItemName}
            </span>
          </div>
        </td>
        <td className={TD_CLASS}>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
              g.type === 'Coupon'
                ? 'bg-violet-100 text-violet-700'
                : 'bg-sky-100 text-sky-700'
            }`}
          >
            {g.type === 'Coupon' ? '쿠폰' : '배송'}
          </span>
        </td>
        <td className={TD_R}>{formatPrice(g.priceRetail)}</td>
        <td className={TD_R}>{formatPrice(g.priceWhole)}</td>
        <td className={TD_R}>
          <span className="text-emerald-600">{g.convertedCount}</span>
          <span className="text-gray-400">/{g.totalCount}</span>
        </td>
        <td className={TD_R}>
          <span className="text-amber-600">{fmtRate(g.windfallRate)}</span>
        </td>
        <td className={TD_R}>{formatPrice(g.totalRevenue)}</td>
        <td className={`${TD_R} text-red-500`}>-{formatPrice(g.totalServiceFee)}</td>
        <td className={TD_R}>{g.totalCogs > 0 ? `-${formatPrice(g.totalCogs)}` : '-'}</td>
        <td className={TD_R}>
          <div className="flex flex-col items-end gap-0 leading-tight">
            <span className="font-medium text-emerald-600">
              {formatPrice(g.convertedProfit)}
            </span>
            <span className="font-normal text-emerald-700/90">
              ({fmtRate(effectiveConvertedProfitRate(g))})
            </span>
          </div>
        </td>
        <td className={TD_R}>
          <div className="flex flex-col items-end gap-0 leading-tight">
            <span className="font-medium text-amber-600">
              {formatPrice(g.windfallProfit)}
            </span>
            <span className="font-normal text-amber-800/90">
              ({fmtRate(effectiveWindfallProfitRate(g))})
            </span>
          </div>
        </td>
        <td className={`${TD_R} font-semibold text-indigo-600`}>
          {formatPrice(g.totalProfit)}
        </td>
        <td className={`${TD_R} font-semibold text-indigo-600`}>
          {fmtRate(g.totalProfitRate)}
        </td>
      </tr>

      {open && hasChoiceUnits && (
        <>
          <tr className="bg-indigo-50/50">
            <td colSpan={COL_SPAN} className="px-3 py-1">
              <div
                className={`flex items-center gap-2 text-[10px] font-semibold text-indigo-600 ${
                  isAgencyAdmin ? 'tracking-normal' : 'uppercase tracking-wider'
                }`}
              >
                <span>선택 옵션별 전환 상세</span>
                <div className="h-px flex-1 bg-indigo-200" />
              </div>
            </td>
          </tr>
          <tr>
            <td
              colSpan={COL_SPAN}
              className="bg-indigo-50/15 border-b border-indigo-100/40 p-0"
            >
              <div className="overflow-x-auto px-2 py-2 pl-3">
                <table className="w-full min-w-[480px] table-fixed border-separate border-spacing-0 text-xs sm:text-sm">
                  <thead>
                    <tr className="bg-indigo-50/50 text-gray-600">
                      <th className="w-[24%] py-1.5 pl-2 pr-1 text-left text-[10px] font-medium text-gray-500 sm:text-xs">
                        옵션명
                      </th>
                      <th className="w-[10%] py-1.5 px-1 text-right text-[10px] font-medium text-gray-500 sm:text-xs">
                        정가
                      </th>
                      <th className="w-[10%] py-1.5 px-1 text-right text-[10px] font-medium text-gray-500 sm:text-xs">
                        도매가
                      </th>
                      <th className="w-[8%] py-1.5 px-1 text-right text-[10px] font-medium text-gray-500 sm:text-xs">
                        전환
                      </th>
                      <th className="w-[12%] py-1.5 px-1 text-right text-[10px] font-medium text-gray-500 sm:text-xs">
                        매출
                      </th>
                      <th className="w-[11%] py-1.5 px-1 text-right text-[10px] font-medium text-gray-500 sm:text-xs">
                        수수료
                      </th>
                      <th className="w-[11%] py-1.5 px-1 text-right text-[10px] font-medium text-gray-500 sm:text-xs">
                        매입
                      </th>
                      <th className="w-[14%] py-1.5 pl-1 pr-2 text-right text-[10px] font-medium text-gray-500 sm:text-xs">
                        <div className="flex flex-col items-end gap-0 leading-tight">
                          <span>수익</span>
                          <span className="font-normal normal-case text-gray-400">
                            (수익률)
                          </span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.choiceUnits.map((cu) => (
                      <ChoiceUnitRow key={cu.choiceUnitId} cu={cu} />
                    ))}
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        </>
      )}
    </>
  );
};

const ChoiceUnitRow: FC<{ cu: ChoiceUnitConvertedRow }> = ({ cu }) => (
  <tr className="border-b border-indigo-100/40 last:border-0 hover:bg-indigo-50/30">
    <td className="overflow-hidden py-1.5 pl-2 pr-1 align-middle">
      <div className="flex min-w-0 flex-nowrap items-center gap-x-1.5">
        <span className="shrink-0 rounded bg-indigo-100 px-1 py-px text-[10px] font-bold text-indigo-600">
          옵션
        </span>
        {cu.brandName ? (
          <span className="max-w-[5rem] shrink-0 truncate rounded-md border border-indigo-200 bg-indigo-50 px-1.5 py-px text-[10px] font-bold text-indigo-900 sm:max-w-[7rem] sm:text-xs">
            {cu.brandName}
          </span>
        ) : null}
        <span
          className="min-w-0 flex-1 truncate text-xs font-medium text-gray-800 sm:text-sm"
          title={cu.displayName}
        >
          {cu.displayName}
        </span>
      </div>
    </td>
    <td className="py-1.5 px-1 text-right tabular-nums text-gray-800">
      {formatPrice(cu.priceRegular)}
    </td>
    <td className="py-1.5 px-1 text-right tabular-nums text-gray-800">
      {formatPrice(cu.priceWhole)}
    </td>
    <td className="py-1.5 px-1 text-right tabular-nums text-emerald-700">{cu.count}</td>
    <td className="py-1.5 px-1 text-right tabular-nums text-gray-800">
      {formatPrice(cu.revenue)}
    </td>
    <td className={`py-1.5 px-1 text-right tabular-nums text-red-500`}>
      -{formatPrice(cu.serviceFee)}
    </td>
    <td className="py-1.5 px-1 text-right tabular-nums text-gray-800">
      {cu.cogs > 0 ? `-${formatPrice(cu.cogs)}` : '-'}
    </td>
    <td className="py-1.5 pl-1 pr-2 text-right">
      <div className="flex flex-col items-end gap-0 leading-tight">
        <span className="font-medium text-emerald-700">{formatPrice(cu.profit)}</span>
        <span className="font-normal text-indigo-700/90">
          ({fmtRate(cu.profitRate ?? 0)})
        </span>
      </div>
    </td>
  </tr>
);

const HighlightCard: FC<{
  label: string;
  value: number;
  rate: number;
  count: number;
  total: number;
  revenue: number;
  revenueLabel: string;
  ratioLabel: string;
  ratio: number;
  color: 'emerald' | 'amber';
}> = ({
  label,
  value,
  rate,
  count,
  total,
  revenue,
  revenueLabel,
  ratioLabel,
  ratio,
  color,
}) => {
  const styles = {
    emerald: {
      border: 'border-emerald-200',
      bg: 'bg-emerald-50',
      label: 'text-emerald-600',
      value: 'text-emerald-700',
      sub: 'text-emerald-500',
      countBg: 'bg-emerald-100',
      countText: 'text-emerald-700',
    },
    amber: {
      border: 'border-amber-200',
      bg: 'bg-amber-50',
      label: 'text-amber-600',
      value: 'text-amber-700',
      sub: 'text-amber-500',
      countBg: 'bg-amber-100',
      countText: 'text-amber-700',
    },
  }[color];

  return (
    <div className={`rounded-lg border ${styles.border} ${styles.bg} px-4 py-4`}>
      <div className="flex items-center justify-between">
        <p className={`text-xs font-medium ${styles.label}`}>{label}</p>
        <span
          className={`rounded-full ${styles.countBg} px-2.5 py-0.5 text-sm font-bold ${styles.countText}`}
        >
          {count}/{total}건
        </span>
      </div>
      <p className={`mt-2 text-2xl font-bold ${styles.value}`}>{formatPrice(value)}원</p>
      <p className="mt-1 text-sm text-gray-500">
        {revenueLabel}{' '}
        <span className="font-semibold text-gray-700">{formatPrice(revenue)}원</span> 대비
      </p>
      <div className={`mt-2 flex items-center gap-3 text-xs ${styles.sub}`}>
        <span>수익률 {fmtRate(rate)}</span>
        <span className="text-gray-300">|</span>
        <span>
          {ratioLabel} {fmtRate(ratio)}
        </span>
      </div>
    </div>
  );
};

const CountBar: FC<{
  label: string;
  count: number;
  total: number;
  color: string;
}> = ({ label, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-8 text-gray-500">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-14 text-right font-medium text-gray-600">
        {count}건 <span className="text-gray-400">({pct.toFixed(0)}%)</span>
      </span>
    </div>
  );
};
